import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, List, Typography } from "@mui/material";
import { FC, useState } from "react";
import ArrowRightIcon from '@/icons/ArrowRight';
import NavigationRefresh from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import { Link as RouterLink, useLocation } from 'react-router-dom'
import CreateBackup from "./CreateBackup";
import { GreenJobs, RedJobs, YellowJobs } from "../dashboard/statusBadge";
import { useEffect } from "react";
import { push } from "connected-react-router";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "@/constants";


export interface StatusCardProps {
  successCount: number | undefined;
  warningCount: number | undefined;
  errorCount: number | undefined;
  onRefresh: () => void;
  title?: string;
  badgeSubtitle?: string;
}

const StatusCard: FC<StatusCardProps> = ({
  successCount = 0,
  warningCount = 0,
  errorCount = 0,
  onRefresh,
  title,
  badgeSubtitle,
  children,
  ...props
}) => {
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0);

  return (
    <Card {...props}>
      <CardHeader
        sx={{ pb: 0 }}
        title={(
          <Typography
            color="textPrimary"
            variant="h6"
          >
            {title}
          </Typography>
        )}
        action={(
          <Button
            size="small"
            startIcon={loading ?<CircularProgress size={14} /> : <NavigationRefresh />}
            color="secondary"
            onClick={onRefresh}
          >
            Refresh
          </Button>)}
      />
      <CardContent>
        <Grid container direction="row" sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography
              color="textSecondary"
              variant="overline"
            >
              {badgeSubtitle}
            </Typography>
            <List
              disablePadding
              sx={{ pt: 1 }}
            >
              <GreenJobs count={successCount} />
              <YellowJobs count={warningCount} />
              <RedJobs count={errorCount} />
            </List>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                mt: 4,
                alignItems: 'normal',
                display: 'flex',
                flexDirection: 'column',
                '& .MuiButton-root': {
                  mb: 1.5,
                },
              }}
            >
              {children}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}


export const BackupStatusCard: FC<StatusCardProps> = (props) => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const location = useLocation()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (location.state && typeof location.state === 'object') {
      const { openCreate }: {openCreate:boolean} = location.state as any
      if (openCreate) {
        setOpen(true)
        dispatch(push(location.pathname, null))
      }
    }
  }, [location, dispatch])

  const [openNote, setOpenNote] = useState(false)

  return (
    <>
      <StatusCard title="Scheduled Backups" badgeSubtitle="Backup Jobs" {...props}>
        <Button
          color="primary"
          endIcon={<ArrowRightIcon fontSize="small" />}
          variant="outlined"
          component={RouterLink}
          to="/backups-n-validations/list?tab=backup"
          >
          View Backup Jobs
        </Button>
        <Button
          color="primary"
          endIcon={<ArrowRightIcon fontSize="small" />}
          variant="outlined"
          onClick={() => setOpenNote(true)}
        >
          View Backup Schedules
        </Button>
        {hasWritePermit &&
          <>
            <Button
              color="primary"
              startIcon={<AddIcon fontSize="small" />}
              variant="outlined"
              onClick={() => setOpen(true)}
            >
              Create Backup Job
            </Button>
            <CreateBackup open={open} setOpen={setOpen} />
          </>}
      </StatusCard>
      <Dialog
        open={openNote}
        onClose={() => setOpenNote(false)}
        aria-labelledby="about-backup-schedule-title"
        aria-describedby="about-backup-schedule-description"
      >
        <DialogTitle id="about-backup-schedule-title">
          About Backup Schedule
          <IconButton
            aria-label="close"
            onClick={() => setOpenNote(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent id="about-backup-schedule-description">
          <Typography
            color="success"
            variant="body1"
          >
            For databases {" "}
            <Typography component="span" variant="body2" sx={{ 
              textDecoration: 'dotted underline'
            }}>
              not hosted 
            </Typography> {" "}
            by OnData, the backup schedule is managed by customers. Customers can schedule different types of backups  to be sent to OnData at different schedules. For more information, please refer to documentation on <RouterLink to="/documents/securedb/database_backup/microsoft sql server">Backup and Validation</RouterLink>.
          </Typography>
          <br />
          <Typography
            color="success"
            variant="body1"
          >
            For databases {" "}
            <Typography component="span" variant="body2" sx={{ 
              textDecoration: 'dotted underline'
            }}>
              hosted 
            </Typography> {" "}
            by OnData, OnData will schedule the backups. The feature to customize the backup schedule will be made available in next release.
            </Typography>
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  )
}

export const ValidationStatusCard: FC<StatusCardProps> = (props) => (
  <StatusCard title="Scheduled Validations" badgeSubtitle="Validation Jobs" {...props}>
    <Button
      color="primary"
      endIcon={<ArrowRightIcon fontSize="small" />}
      variant="outlined"
      component={RouterLink}
      to="/backups-n-validations/list?tab=validation"
    >
      View Validation Jobs
    </Button>
  </StatusCard>
)

